import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default permissions structure
const DEFAULT_PERMISSIONS = {
  edit_subscribers: false,
  delete_subscribers: false,
  edit_payments: false,
  delete_payments: false,
  edit_invoices: false,
  delete_invoices: false,
  edit_expenses: false,
  delete_expenses: false,
  confirm_deposits: false,
  send_announcements: false,
  manage_users: false
};

// Flag to prevent multiple logout toasts
let hasShownExpiredToast = false;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Setup axios interceptor for handling token expiration
  useEffect(() => {
    // Helper: normalise a Pydantic v2 validation `detail` (an array of
    // {type, loc, msg, input, url} objects) into a readable string so
    // downstream `toast.error(detail)` calls can never receive an object
    // and crash React with "Objects are not valid as a React child".
    const normaliseDetail = (detail) => {
      if (detail == null) return detail;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        return detail
          .map((d) => {
            if (typeof d === 'string') return d;
            const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
            const msg = d?.msg || d?.message || JSON.stringify(d);
            return loc ? `${loc}: ${msg}` : msg;
          })
          .join('; ');
      }
      if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
      return String(detail);
    };

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Always coerce validation/error detail to a string before any
        // caller's catch block reads it (caller code does
        // `toast.error(err.response?.data?.detail)` which would otherwise
        // be handed an array of objects on 422 responses).
        try {
          if (error?.response?.data?.detail !== undefined) {
            error.response.data.detail = normaliseDetail(error.response.data.detail);
          }
        } catch {
          // best-effort
        }

        if (error.response?.status === 401) {
          const errorMessage = error.response?.data?.detail || '';
          const url = (error.config?.url || '').toLowerCase();
          
          // Only treat as a *real* expired session if the message says so
          // AND the failing request was an auth-validating endpoint.
          const looksLikeExpired = 
            errorMessage.toLowerCase().includes('expired') || 
            errorMessage.toLowerCase().includes('invalid authentication') ||
            errorMessage.toLowerCase().includes('could not validate');
          const isAuthEndpoint = 
            url.includes('/auth/') || url.endsWith('/me') || url.includes('/users/me');
          
          if (looksLikeExpired && isAuthEndpoint) {
            // Clear the stored token and user
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
            
            // Show toast only once
            if (!hasShownExpiredToast) {
              hasShownExpiredToast = true;
              toast.error('Your session has expired. Please login again.', {
                duration: 5000,
                onDismiss: () => { hasShownExpiredToast = false; }
              });
              setTimeout(() => { hasShownExpiredToast = false; }, 5000);
            }
            
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/';
            }
          } else if (looksLikeExpired) {
            if (!hasShownExpiredToast) {
              hasShownExpiredToast = true;
              toast.error('Session expired — please save your work and login again.', { duration: 6000 });
              setTimeout(() => { hasShownExpiredToast = false; }, 5000);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      // Ensure permissions exist
      if (!userData.permissions) {
        userData.permissions = userData.role === 'admin' 
          ? Object.keys(DEFAULT_PERMISSIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {})
          : { ...DEFAULT_PERMISSIONS };
      }
      
      // Ensure nas_access is a proper boolean
      if (userData.role === 'admin') {
        userData.nas_access = true;
      } else {
        // Convert any truthy value to boolean
        userData.nas_access = userData.nas_access === true || 
          userData.nas_access === "true" || 
          userData.nas_access === 1 ||
          userData.nas_access === "1";
      }
      
      // Debug log
      console.log('FetchUser - NAS Access:', {
        username: userData.username,
        role: userData.role,
        nas_access_raw: response.data?.nas_access,
        nas_access_converted: userData.nas_access
      });
      
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user: userData } = response.data;
      
      // Ensure permissions exist
      if (!userData.permissions) {
        userData.permissions = userData.role === 'admin' 
          ? Object.keys(DEFAULT_PERMISSIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {})
          : { ...DEFAULT_PERMISSIONS };
      }
      
      // Ensure nas_access is a proper boolean
      if (userData.role === 'admin') {
        userData.nas_access = true;
      } else {
        // Convert any truthy value to boolean
        userData.nas_access = userData.nas_access === true || 
          userData.nas_access === "true" || 
          userData.nas_access === 1 ||
          userData.nas_access === "1";
      }
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userData };
    } catch (error) {
      // Build a precise, user-friendly error message.
      let message;
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail; // already normalised by the interceptor
        if (typeof detail === 'string' && detail.trim()) {
          message = detail;
        } else if (status === 401) {
          message = 'Incorrect username or password.';
        } else if (status === 403) {
          message = 'Account access denied. Contact your admin.';
        } else if (status >= 500) {
          message = `Server error (${status}). Please try again in a moment or contact admin.`;
        } else {
          message = `Login failed (status ${status}). Please try again.`;
        }
      } else if (error.request) {
        // No response at all — network / CORS / offline / proxy
        message = "Can't reach the server. Check your internet connection and try again.";
      } else {
        message = error.message || 'Login failed';
      }
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Helper function to check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admin always has all permissions
    return user.permissions?.[permission] === true;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, isAuthenticated: !!user, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};